import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import TextEditor from 'flarum/common/components/TextEditor';
import UploadButton from './components/UploadButton';
import DragAndDrop from './components/DragAndDrop';
import PasteClipboard from './components/PasteClipboard';
import Uploader from './handler/Uploader';
import FileManagerButton from './components/FileManagerButton';

/**
 * 判断 composer 是否已关闭（用户发布了帖子或取消了撰写）。
 * composer.hide() -> clear() 会把 editor destroy 并置 null。
 */
function isComposerGone() {
  const composer = app.composer;
  return !composer || !composer.editor || composer.position === 'hidden';
}

/**
 * 尝试定位"刚发布的帖子"（用于后台上传完成后把文件自动插入进去）。
 *
 * 不使用固定时间窗口（旧版 10 分钟限制在慢速网络下会失效）：
 * 发帖提交瞬间 index.js 已在 uploader.pendingPublishAt 记录锚点时间戳，
 * 这里找「锚点之后当前用户创建的第一个讨论」——无论多久传完都能命中。
 *
 * @param {object} uploader fof-upload 的 Uploader 实例（携带 pendingPublishAt）
 * @returns {Promise<null|object>} post 模型或 null
 */
function resolveLatestOwnPost(uploader) {
  const user = app.session.user;
  if (!user) return Promise.resolve(null);

  // 锚点 = 发帖提交时刻；找不到时退回"10分钟前"作为保守兜底
  const anchor = (uploader && uploader.pendingPublishAt) || Date.now() - 10 * 60 * 1000;
  const SLACK = 5 * 60 * 1000; // 锚点前容差 5 分钟（时钟误差）

  // 路径 1: 当前讨论页
  let discussion = null;
  try {
    if (app.current && typeof app.current.get === 'function') {
      discussion = app.current.get('discussion') || null;
    } else if (app.current && app.current.discussion) {
      discussion = app.current.discussion;
    }
  } catch (e) {
    discussion = null;
  }

  const isOwnAfterAnchor = (d) => {
    try {
      if (!d || typeof d.user !== 'function' || !d.user() || d.user().id() !== user.id()) return false;
      const created = d.createdAt();
      if (!created) return false;
      // 帖子创建时间在锚点之后（含容差），无上限时间窗口 → 传多久都找得到
      return created.getTime() >= anchor - SLACK;
    } catch (e) {
      return false;
    }
  };

  const getFirstPost = (d) => {
    try {
      if (!d || typeof d.firstPost !== 'function') return null;
      return d.firstPost() || null;
    } catch (e) {
      return null;
    }
  };

  if (discussion && isOwnAfterAnchor(discussion)) {
    const post = getFirstPost(discussion);
    if (post) return Promise.resolve(post);

    return app.store
      .find('discussions', discussion.id(), { include: 'firstPost' })
      .then((d) => {
        const p = d && typeof d.firstPost === 'function' ? d.firstPost() : null;
        return p || null;
      })
      .catch(() => null);
  }

  // 路径 2: store 兜底 - 锚点之后创建的本人讨论，取创建时间最接近锚点的那条
  try {
    const candidates = app.store
      .all('discussions')
      .filter((d) => isOwnAfterAnchor(d))
      .sort((a, b) => {
        const ta = a.createdAt() ? a.createdAt().getTime() : 0;
        const tb = b.createdAt() ? b.createdAt().getTime() : 0;
        return ta - tb; // 升序：最早创建（最接近锚点）的优先
      });

    if (candidates.length > 0) {
      const best = candidates[0];
      const post = getFirstPost(best);
      if (post) return Promise.resolve(post);

      return app.store
        .find('discussions', best.id(), { include: 'firstPost' })
        .then((d) => {
          const p = d && typeof d.firstPost === 'function' ? d.firstPost() : null;
          return p || null;
        })
        .catch(() => null);
    }
  } catch (e) {
    // ignore
  }

  return Promise.resolve(null);
}

export default function () {
  extend(TextEditor.prototype, 'oninit', function () {
    this.uploader = new Uploader();
  });
  extend(TextEditor.prototype, 'controlItems', function (items) {
    if (!app.forum.attribute('fof-upload.canUpload')) return;

    const composerButtonVisiblity = app.forum.attribute('fof-upload.composerButtonVisiblity');

    // Add media button
    if (composerButtonVisiblity === 'both' || composerButtonVisiblity === 'media-btn') {
      items.add(
        'fof-upload-media',
        FileManagerButton.component({
          uploader: this.uploader,
        })
      );
    }

    // Add upload button
    if (composerButtonVisiblity === 'both' || composerButtonVisiblity === 'upload-btn') {
      items.add(
        'fof-upload',
        UploadButton.component({
          uploader: this.uploader,
        })
      );
    }
  });

  extend(TextEditor.prototype, 'oncreate', function (f_, vnode) {
    if (!app.forum.attribute('fof-upload.canUpload')) return;

    const uploader = this.uploader;

    // 后台插入队列（上传开始时 composer 可见 = 发帖场景）
    uploader.pendingBbcode = null;
    uploader.pendingFiles = null;

    uploader.on('success', ({ file, addBBcode }) => {
      // 情形 A：composer 仍可用（用户还没发布）→ 照旧插入光标处
      if (!isComposerGone()) {
        // 媒体库/分享等 addBBcode=false 的场景不插入正文
        if (!addBBcode) return;
        app.composer.editor.insertAtCursor(file.bbcode() + '\n', false);

        if (typeof this.attrs.preview === 'function') {
          const originalIsFullScreen = app.composer.isFullScreen;
          app.composer.isFullScreen = () => false;
          this.attrs.preview();
          app.composer.isFullScreen = originalIsFullScreen;
        }
        return;
      }

      // 情形 B：composer 已关闭（用户已发布/已离开编辑）→ 收集，等整批完成后自动插入末尾
      // 只收集会在正文里展示的文件（addBBcode 或纯链接/媒体均可）
      if (addBBcode === false && !file.bbcode) return;
      if (!uploader.pendingBbcode) {
        uploader.pendingBbcode = [];
        uploader.pendingFiles = [];
      }
      uploader.pendingBbcode.push(file.bbcode ? file.bbcode() : file.url());
      uploader.pendingFiles.push(file);
    });

    // 一批全部完成后：
    // 1. 若用户在"先发布后上传"场景且文件成功 → 自动把文件插入刚发布帖子的末尾（带 uploadComplete 触发后端 AI 审核公开）
    // 2. 若发布时在上传但被取消/失败（无成功文件）→ 也对帖子做一次空 finalize（纯文本 AI 审核解锁公开）
    uploader.on('uploaded', () => {
      const hadPendingPublish = uploader.pendingPublish;
      const hasBbcode = uploader.pendingBbcode && uploader.pendingBbcode.length > 0;

      if (!hasBbcode && !hadPendingPublish) return;

      const bbcodeList = uploader.pendingBbcode ? uploader.pendingBbcode.slice() : [];
      const fileList = uploader.pendingFiles ? uploader.pendingFiles.slice() : [];
      uploader.pendingBbcode = null;
      uploader.pendingFiles = null;
      uploader.pendingPublish = false;

      resolveLatestOwnPost(uploader).then((post) => {
        if (!post) {
          if (hasBbcode) {
            app.alerts.show({ type: 'info' }, `文件已上传完成并保存到「我的上传」，未能自动插入帖子（未能定位到刚发布的帖子，可手动编辑帖子插入）。`);
          }
          return;
        }

        const oldContent = post.content() || '';
        const block = bbcodeList.join('\n');
        // 有成功文件 → 追加；取消/失败 → 内容不变，仅带 uploadComplete 触发重新审核公开
        const newContent = hasBbcode ? (oldContent ? oldContent + '\n\n' + block : block) : oldContent;

        post
          .save({ content: newContent, uploadComplete: true })
          .then(() => {
            if (hasBbcode) {
              app.alerts.show({ type: 'success' }, `已自动将 ${fileList.length} 个文件插入到帖子末尾喵~`);
            } else {
              app.alerts.show({ type: 'info' }, '上传已取消，帖子已恢复可见喵~');
            }

            if (app.current && typeof app.current.refresh === 'function') {
              app.current.refresh().then(() => m.redraw());
            } else {
              m.redraw();
            }
          })
          .catch(() => {
            if (hasBbcode) {
              app.alerts.show({ type: 'error' }, '自动插入帖子失败，文件已保存到「我的上传」，可手动插入。');
            }
          });
      });
    });

    // Gracefully fail if the TextEditor was used in a non-Composer context
    const dragAndDropTarget = this.fofUploadDragAndDropTarget();

    if (dragAndDropTarget) {
      this.dragAndDrop = new DragAndDrop((files) => this.uploader.upload(files), dragAndDropTarget);
    }

    new PasteClipboard((files) => this.uploader.upload(files), this.$('.TextEditor-editor')[0]);
  });

  extend(TextEditor.prototype, 'onremove', function (f_, vnode) {
    if (!app.forum.attribute('fof-upload.canUpload')) return;

    if (this.dragAndDrop) {
      this.dragAndDrop.unload();
    }
  });

  TextEditor.prototype.fofUploadDragAndDropTarget = function () {
    return this.$().parents('.Composer')[0];
  };
}
